import React, { useState, memo, Component } from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon, ChevronRightIcon, ChevronDownIcon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
const RootCauseAnalysisDashboard: React.FC = () => {
  const [expandedCause, setExpandedCause] = useState<string | null>('LED Headlamp Failure');
  const kpis = [{
    title: 'Root Causes Identified',
    value: '47',
    change: '+8',
    isPositive: true
  }, {
    title: 'Repeat Failure Rate',
    value: '14.8%',
    change: '-2.3%',
    isPositive: true
  }, {
    title: 'CAPA Actions Implemented',
    value: '32',
    change: '+12',
    isPositive: true
  }];
  const defectParetoData = [{
    defect: 'Voltage Fluctuation',
    count: 485,
    cost: 85.2,
    percentage: 28.5,
    cumulative: 28.5
  }, {
    defect: 'Software Bug',
    count: 425,
    cost: 74.5,
    percentage: 25.0,
    cumulative: 53.5
  }, {
    defect: 'Dust Ingress',
    count: 298,
    cost: 52.3,
    percentage: 17.5,
    cumulative: 71.0
  }, {
    defect: 'Temperature Variation',
    count: 215,
    cost: 37.8,
    percentage: 12.6,
    cumulative: 83.6
  }, {
    defect: 'Vibration Damage',
    count: 142,
    cost: 24.9,
    percentage: 8.3,
    cumulative: 91.9
  }, {
    defect: 'Moisture Exposure',
    count: 98,
    cost: 17.2,
    percentage: 5.8,
    cumulative: 97.7
  }, {
    defect: 'Others',
    count: 40,
    cost: 7.0,
    percentage: 2.3,
    cumulative: 100.0
  }];
  const fiveWhyData = [{
    issue: 'LED Headlamp Failure',
    why1: 'Why did the LED headlamp fail?',
    answer1: 'LED driver circuit burned out',
    why2: 'Why did the driver circuit burn out?',
    answer2: 'Excessive voltage spike from alternator',
    why3: 'Why was there a voltage spike?',
    answer3: 'Poor voltage regulation in vehicle electrical system',
    why4: 'Why was voltage regulation poor?',
    answer4: 'Inadequate surge protection in wiring harness',
    why5: 'Why was surge protection inadequate?',
    answer5: 'Design specification did not account for Indian road conditions with frequent voltage fluctuations',
    rootCause: 'Design specification gap - Missing surge protection requirements for Indian market conditions',
    correctiveAction: 'Redesign LED driver with enhanced surge protection (TVS diodes + capacitor bank)',
    preventiveAction: 'Update design standards to include voltage spike testing as per Indian automotive conditions',
    affectedSKUs: ['LED-HL-2024-M', 'LED-HL-2023-K', 'LED-HL-2024-H'],
    estimatedCost: 12.5,
    implementationTime: '3 months'
  }, {
    issue: 'Battery Drain',
    why1: 'Why is the battery draining?',
    answer1: 'Excessive current draw when vehicle is off',
    why2: 'Why is there excessive current draw?',
    answer2: 'ECU not entering sleep mode properly',
    why3: 'Why is ECU not sleeping?',
    answer3: 'Software bug in power management routine',
    why4: 'Why does the bug exist?',
    answer4: 'Inadequate testing of sleep mode transitions',
    why5: 'Why was testing inadequate?',
    answer5: 'Test protocol did not include extended soak time scenarios',
    rootCause: 'Testing gap - Sleep mode validation insufficient for real-world usage patterns',
    correctiveAction: 'Deploy firmware update v2.4 with corrected power management logic',
    preventiveAction: 'Enhance test protocol to include 72-hour soak tests with periodic wake events',
    affectedSKUs: ['BATT-SYS-2023-H', 'ECU-PWR-2024-T', 'ECU-PWR-2023-M'],
    estimatedCost: 8.2,
    implementationTime: '6 weeks'
  }, {
    issue: 'Sensor Malfunction',
    why1: 'Why is the sensor malfunctioning?',
    answer1: 'Sensor giving erratic readings',
    why2: 'Why are readings erratic?',
    answer2: 'Dust accumulation on sensor surface',
    why3: 'Why is dust accumulating?',
    answer3: 'Inadequate sealing of sensor housing',
    why4: 'Why is sealing inadequate?',
    answer4: 'Gasket material degrading in high temperature',
    why5: 'Why is gasket degrading?',
    answer5: 'Wrong material specification for Indian climate (40-50°C ambient)',
    rootCause: 'Material selection error - Gasket not rated for Indian temperature range',
    correctiveAction: 'Replace gasket material with high-temp silicone (rated to 150°C)',
    preventiveAction: 'Update material selection criteria to mandate high-temp testing for all sealing components',
    affectedSKUs: ['SENS-MOD-2024-K', 'SENS-MOD-2023-H', 'SENS-PKG-2024-M'],
    estimatedCost: 6.8,
    implementationTime: '8 weeks'
  }];
  const failureHotspotData = [{
    system: 'Electrical System',
    components: [{
      name: 'LED Headlamp',
      failures: 485,
      severity: 'Critical'
    }, {
      name: 'Wiring Harness',
      failures: 328,
      severity: 'High'
    }, {
      name: 'Battery System',
      failures: 425,
      severity: 'Critical'
    }, {
      name: 'Alternator',
      failures: 156,
      severity: 'Medium'
    }]
  }, {
    system: 'Electronic Control',
    components: [{
      name: 'ECU Module',
      failures: 298,
      severity: 'High'
    }, {
      name: 'Sensor Module',
      failures: 398,
      severity: 'High'
    }, {
      name: 'Display Unit',
      failures: 142,
      severity: 'Medium'
    }, {
      name: 'Switch Assembly',
      failures: 89,
      severity: 'Low'
    }]
  }, {
    system: 'Body & Trim',
    components: [{
      name: 'Door Handle',
      failures: 342,
      severity: 'High'
    }, {
      name: 'Tail Lamp',
      failures: 365,
      severity: 'High'
    }, {
      name: 'Interior Trim',
      failures: 285,
      severity: 'Medium'
    }, {
      name: 'Exterior Trim',
      failures: 178,
      severity: 'Medium'
    }]
  }, {
    system: 'HVAC System',
    components: [{
      name: 'Blower Motor',
      failures: 268,
      severity: 'Medium'
    }, {
      name: 'AC Compressor',
      failures: 195,
      severity: 'Medium'
    }, {
      name: 'Heater Core',
      failures: 125,
      severity: 'Low'
    }, {
      name: 'Climate Control',
      failures: 98,
      severity: 'Low'
    }]
  }];
  const rootCauseCategoriesData = [{
    category: 'Design Issues',
    count: 12,
    percentage: 25.5,
    totalCost: 218.5,
    linkedSKUs: [{
      sku: 'LED-HL-2024-M',
      product: 'LED Headlamp Module',
      rootCause: 'Inadequate surge protection design',
      failures: 485,
      cost: 85.2,
      status: 'CAPA In Progress'
    }, {
      sku: 'SENS-MOD-2024-K',
      product: 'Parking Sensor Module',
      rootCause: 'Gasket material not rated for temperature',
      failures: 398,
      cost: 69.8,
      status: 'CAPA Completed'
    }, {
      sku: 'DOOR-HDL-2024-M',
      product: 'Door Handle Assembly',
      rootCause: 'Locking mechanism design flaw',
      failures: 342,
      cost: 60.1,
      status: 'CAPA In Progress'
    }]
  }, {
    category: 'Software/Firmware',
    count: 8,
    percentage: 17.0,
    totalCost: 156.8,
    linkedSKUs: [{
      sku: 'BATT-SYS-2023-H',
      product: 'Battery Management System',
      rootCause: 'Power management software bug',
      failures: 425,
      cost: 74.5,
      status: 'CAPA In Progress'
    }, {
      sku: 'ECU-PWR-2024-T',
      product: 'ECU Power Module',
      rootCause: 'Sleep mode transition logic error',
      failures: 298,
      cost: 52.3,
      status: 'CAPA Planned'
    }, {
      sku: 'DISP-UNIT-2023-K',
      product: 'Display Unit',
      rootCause: 'Firmware memory leak',
      failures: 172,
      cost: 30.0,
      status: 'CAPA Completed'
    }]
  }, {
    category: 'Manufacturing Process',
    count: 10,
    percentage: 21.3,
    totalCost: 189.2,
    linkedSKUs: [{
      sku: 'TAIL-LP-2023-T',
      product: 'Tail Lamp Assembly',
      rootCause: 'Inadequate soldering process control',
      failures: 365,
      cost: 64.2,
      status: 'CAPA In Progress'
    }, {
      sku: 'WIRE-HRN-2023-K',
      product: 'Wiring Harness',
      rootCause: 'Crimp quality inconsistency',
      failures: 328,
      cost: 57.6,
      status: 'CAPA In Progress'
    }, {
      sku: 'INT-TRM-2024-H',
      product: 'Interior Trim Panel',
      rootCause: 'Injection molding temperature variation',
      failures: 285,
      cost: 50.1,
      status: 'CAPA Completed'
    }, {
      sku: 'HVAC-BLW-2023-T',
      product: 'HVAC Blower Motor',
      rootCause: 'Bearing assembly tolerance issue',
      failures: 268,
      cost: 47.2,
      status: 'CAPA Planned'
    }]
  }, {
    category: 'Material Quality',
    count: 7,
    percentage: 14.9,
    totalCost: 128.4,
    linkedSKUs: [{
      sku: 'BRAK-PAD-2024-V',
      product: 'Brake Pad Assembly',
      rootCause: 'Friction material composition variation',
      failures: 245,
      cost: 43.1,
      status: 'CAPA In Progress'
    }, {
      sku: 'FUEL-PMP-2023-M',
      product: 'Fuel Pump Module',
      rootCause: 'Impeller material degradation',
      failures: 228,
      cost: 40.1,
      status: 'CAPA Planned'
    }, {
      sku: 'SEAL-KIT-2024-H',
      product: 'Seal Kit',
      rootCause: 'Rubber compound aging',
      failures: 198,
      cost: 34.8,
      status: 'CAPA Completed'
    }]
  }, {
    category: 'Installation/Assembly',
    count: 6,
    percentage: 12.8,
    totalCost: 98.5,
    linkedSKUs: [{
      sku: 'STRUT-ASM-2023-T',
      product: 'Strut Assembly',
      rootCause: 'Improper torque specification',
      failures: 185,
      cost: 32.5,
      status: 'CAPA In Progress'
    }, {
      sku: 'EXHST-SYS-2024-K',
      product: 'Exhaust System',
      rootCause: 'Mounting bracket misalignment',
      failures: 168,
      cost: 29.5,
      status: 'CAPA Planned'
    }, {
      sku: 'STEER-COL-2023-H',
      product: 'Steering Column',
      rootCause: 'Connector installation error',
      failures: 152,
      cost: 26.7,
      status: 'CAPA Completed'
    }]
  }, {
    category: 'Environmental Factors',
    count: 4,
    percentage: 8.5,
    totalCost: 67.2,
    linkedSKUs: [{
      sku: 'FILTER-AIR-2024-M',
      product: 'Air Filter Assembly',
      rootCause: 'Dust exposure beyond design limits',
      failures: 142,
      cost: 24.9,
      status: 'CAPA Planned'
    }, {
      sku: 'COOL-RAD-2023-K',
      product: 'Cooling Radiator',
      rootCause: 'Corrosion from water quality',
      failures: 125,
      cost: 22.0,
      status: 'CAPA In Progress'
    }, {
      sku: 'PAINT-EXT-2024-H',
      product: 'Exterior Paint',
      rootCause: 'UV degradation in high sun exposure',
      failures: 98,
      cost: 17.2,
      status: 'CAPA Completed'
    }]
  }];
  const getFailureSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical':
        return 'bg-red-600';
      case 'High':
        return 'bg-orange-500';
      case 'Medium':
        return 'bg-yellow-500';
      default:
        return 'bg-green-500';
    }
  };
  const getStatusColor = (status: string) => {
    if (status.includes('Completed')) return 'text-green-600 bg-green-50 border-green-300';
    if (status.includes('Progress')) return 'text-blue-600 bg-blue-50 border-blue-300';
    return 'text-orange-600 bg-orange-50 border-orange-300';
  };
  const toggleCause = (issue: string) => {
    setExpandedCause(expandedCause === issue ? null : issue);
  };
  return <div data-id="element-456">
      <DescriptionPanel title="Root Cause Analysis & Failure Investigation" description="Comprehensive root cause analysis using 5-Why methodology, Pareto analysis, and failure hotspot mapping. Identify systemic issues, track CAPA implementation, and prevent recurring failures through data-driven insights." insights={['47 unique root causes identified with 32 CAPA actions already implemented', 'Top 3 defect types (Voltage, Software, Dust) account for 71% of all failures', 'Design issues represent 25.5% of root causes - highest impact category', 'Repeat failure rate reduced to 14.8% through systematic root cause elimination']} data-id="element-457" />
      {/* KPI Tiles */}
      <div className="mb-8" data-id="element-458">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-459">
          Root Cause Analysis Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-460">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-461">
              <div className="flex flex-col h-full" data-id="element-462">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-463">
                  {kpi.title}
                </div>
                <div className="mt-3 text-4xl font-bold text-gray-900" data-id="element-464">
                  {kpi.value}
                </div>
                <div className={`mt-2 text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-465">
                  {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-466" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-467" />}
                  {kpi.change} vs last quarter
                </div>
              </div>
            </Card>)}
        </div>
      </div>
      {/* Defect Pareto Chart */}
      <Card className="p-6 mb-8" data-id="element-468">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-469">
          Defect Pareto Analysis - 80/20 Rule
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-470">
          Focus on top defects for maximum impact - 80% of failures from 20% of
          root causes
        </p>
        <ResponsiveContainer width="100%" height={400} data-id="element-471">
          <ComposedChart data={defectParetoData} data-id="element-472">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-473" />
            <XAxis dataKey="defect" tick={{
            fontSize: 11
          }} angle={-15} textAnchor="end" height={100} data-id="element-474" />
            <YAxis yAxisId="left" tick={{
            fontSize: 12
          }} label={{
            value: 'Failure Count',
            angle: -90,
            position: 'insideLeft'
          }} data-id="element-475" />
            <YAxis yAxisId="right" orientation="right" tick={{
            fontSize: 12
          }} domain={[0, 100]} label={{
            value: 'Cumulative %',
            angle: 90,
            position: 'insideRight'
          }} data-id="element-476" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-477" />
            <Legend data-id="element-478" />
            <Bar yAxisId="left" dataKey="count" fill="#3b82f6" name="Failure Count" data-id="element-479">
              {defectParetoData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.cumulative <= 80 ? '#ef4444' : '#3b82f6'} data-id="element-480" />)}
            </Bar>
            <Line yAxisId="right" type="monotone" dataKey="cumulative" stroke="#f59e0b" strokeWidth={3} name="Cumulative %" dot={{
            r: 5,
            fill: '#f59e0b'
          }} data-id="element-481" />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-482">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-483">
            <div className="text-center" data-id="element-484">
              <div className="text-gray-600" data-id="element-485">Total Defects</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-486">1,703</div>
            </div>
            <div className="text-center" data-id="element-487">
              <div className="text-gray-600" data-id="element-488">Top 3 Defects</div>
              <div className="text-xl font-bold text-red-600" data-id="element-489">71.0%</div>
            </div>
            <div className="text-center" data-id="element-490">
              <div className="text-gray-600" data-id="element-491">80% Threshold</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-492">4 defects</div>
            </div>
            <div className="text-center" data-id="element-493">
              <div className="text-gray-600" data-id="element-494">Total Cost Impact</div>
              <div className="text-xl font-bold text-red-600" data-id="element-495">₹298.9 L</div>
            </div>
          </div>
        </div>
      </Card>
      {/* 5-Why Tree Structure */}
      <Card className="p-6 mb-8" data-id="element-496">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-497">
          5-Why Root Cause Analysis Tree
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-498">
          Systematic investigation methodology to identify true root causes
        </p>
        <div className="space-y-4" data-id="element-499">
          {fiveWhyData.map((item, index) => <div key={index} className="border-2 border-gray-200 rounded-lg overflow-hidden" data-id="element-500">
              <button onClick={() => toggleCause(item.issue)} className="w-full p-4 bg-gradient-to-r from-blue-50 to-white flex items-center justify-between hover:from-blue-100 hover:to-blue-50 transition-colors" data-id="element-501">
                <div className="flex items-center" data-id="element-502">
                  <AlertTriangleIcon size={20} className="text-red-600 mr-3" data-id="element-503" />
                  <div className="text-left" data-id="element-504">
                    <div className="font-bold text-gray-900" data-id="element-505">{item.issue}</div>
                    <div className="text-sm text-gray-600" data-id="element-506">
                      {item.affectedSKUs.length} SKUs affected • ₹
                      {item.estimatedCost}L cost impact
                    </div>
                  </div>
                </div>
                {expandedCause === item.issue ? <ChevronDownIcon size={20} className="text-gray-500" data-id="element-507" /> : <ChevronRightIcon size={20} className="text-gray-500" data-id="element-508" />}
              </button>
              {expandedCause === item.issue && <div className="p-6 bg-white" data-id="element-509">
                  <div className="space-y-6" data-id="element-510">
                    {/* 5 Why Steps */}
                    <div className="relative pl-8 space-y-4" data-id="element-511">
                      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-300" data-id="element-512" />
                      <div className="relative" data-id="element-513">
                        <div className="absolute -left-8 top-1 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold" data-id="element-514">
                          1
                        </div>
                        <div className="bg-blue-50 p-3 rounded-lg" data-id="element-515">
                          <div className="text-sm font-semibold text-blue-900 mb-1" data-id="element-516">
                            {item.why1}
                          </div>
                          <div className="text-sm text-blue-700" data-id="element-517">
                            {item.answer1}
                          </div>
                        </div>
                      </div>
                      <div className="relative" data-id="element-518">
                        <div className="absolute -left-8 top-1 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold" data-id="element-519">
                          2
                        </div>
                        <div className="bg-blue-50 p-3 rounded-lg" data-id="element-520">
                          <div className="text-sm font-semibold text-blue-900 mb-1" data-id="element-521">
                            {item.why2}
                          </div>
                          <div className="text-sm text-blue-700" data-id="element-522">
                            {item.answer2}
                          </div>
                        </div>
                      </div>
                      <div className="relative" data-id="element-523">
                        <div className="absolute -left-8 top-1 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold" data-id="element-524">
                          3
                        </div>
                        <div className="bg-blue-50 p-3 rounded-lg" data-id="element-525">
                          <div className="text-sm font-semibold text-blue-900 mb-1" data-id="element-526">
                            {item.why3}
                          </div>
                          <div className="text-sm text-blue-700" data-id="element-527">
                            {item.answer3}
                          </div>
                        </div>
                      </div>
                      <div className="relative" data-id="element-528">
                        <div className="absolute -left-8 top-1 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold" data-id="element-529">
                          4
                        </div>
                        <div className="bg-blue-50 p-3 rounded-lg" data-id="element-530">
                          <div className="text-sm font-semibold text-blue-900 mb-1" data-id="element-531">
                            {item.why4}
                          </div>
                          <div className="text-sm text-blue-700" data-id="element-532">
                            {item.answer4}
                          </div>
                        </div>
                      </div>
                      <div className="relative" data-id="element-533">
                        <div className="absolute -left-8 top-1 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center text-xs font-bold" data-id="element-534">
                          5
                        </div>
                        <div className="bg-red-50 p-3 rounded-lg border-2 border-red-300" data-id="element-535">
                          <div className="text-sm font-semibold text-red-900 mb-1" data-id="element-536">
                            {item.why5}
                          </div>
                          <div className="text-sm text-red-700" data-id="element-537">
                            {item.answer5}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Root Cause */}
                    <div className="bg-gradient-to-r from-red-100 to-red-50 border-2 border-red-400 rounded-lg p-4" data-id="element-538">
                      <div className="text-xs font-bold text-red-600 uppercase mb-2" data-id="element-539">
                        ⚠ Root Cause Identified
                      </div>
                      <div className="text-sm font-bold text-red-900" data-id="element-540">
                        {item.rootCause}
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="grid grid-cols-2 gap-4" data-id="element-541">
                      <div className="bg-green-50 border border-green-300 rounded-lg p-4" data-id="element-542">
                        <div className="text-xs font-bold text-green-700 uppercase mb-2" data-id="element-543">
                          Corrective Action
                        </div>
                        <div className="text-sm text-green-900" data-id="element-544">
                          {item.correctiveAction}
                        </div>
                      </div>
                      <div className="bg-blue-50 border border-blue-300 rounded-lg p-4" data-id="element-545">
                        <div className="text-xs font-bold text-blue-700 uppercase mb-2" data-id="element-546">
                          Preventive Action
                        </div>
                        <div className="text-sm text-blue-900" data-id="element-547">
                          {item.preventiveAction}
                        </div>
                      </div>
                    </div>
                    {/* Affected SKUs and Timeline */}
                    <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200" data-id="element-548">
                      <div data-id="element-549">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-550">
                          Affected SKUs
                        </div>
                        <div className="flex flex-wrap gap-1" data-id="element-551">
                          {item.affectedSKUs.map((sku, skuIndex) => <span key={skuIndex} className="inline-flex px-2 py-1 bg-gray-100 text-gray-700 text-xs font-mono rounded" data-id="element-552">
                              {sku}
                            </span>)}
                        </div>
                      </div>
                      <div data-id="element-553">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-554">
                          Cost Impact
                        </div>
                        <div className="text-lg font-bold text-red-600" data-id="element-555">
                          ₹{item.estimatedCost}L
                        </div>
                      </div>
                      <div data-id="element-556">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-557">
                          Implementation Time
                        </div>
                        <div className="text-lg font-bold text-gray-900" data-id="element-558">
                          {item.implementationTime}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>)}
        </div>
      </Card>
      {/* Failure Hotspot Heatmap */}
      <Card className="p-6 mb-8" data-id="element-559">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-560">
          Failure Hotspot Heatmap by System & Component
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-561">
          Visual identification of high-failure components requiring immediate
          attention
        </p>
        <div className="space-y-6" data-id="element-562">
          {failureHotspotData.map((system, sysIndex) => <div key={sysIndex} className="border border-gray-200 rounded-lg p-4" data-id="element-563">
              <h4 className="text-md font-bold text-gray-800 mb-3" data-id="element-564">
                {system.system}
              </h4>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-565">
                {system.components.map((component, compIndex) => {
              const maxFailures = Math.max(...failureHotspotData.flatMap(s => s.components.map(c => c.failures)));
              const intensity = component.failures / maxFailures * 100;
              return <div key={compIndex} className="relative overflow-hidden rounded-lg border-2 border-gray-300 p-4 hover:shadow-lg transition-shadow" style={{
                backgroundColor: component.severity === 'Critical' ? `rgba(239, 68, 68, ${intensity / 100})` : component.severity === 'High' ? `rgba(249, 115, 22, ${intensity / 100})` : component.severity === 'Medium' ? `rgba(234, 179, 8, ${intensity / 100})` : `rgba(34, 197, 94, ${intensity / 100})`
              }} data-id="element-566">
                      <div className="relative z-10" data-id="element-567">
                        <div className="text-xs font-semibold text-gray-800 mb-1" data-id="element-568">
                          {component.name}
                        </div>
                        <div className="text-2xl font-bold text-gray-900 mb-1" data-id="element-569">
                          {component.failures}
                        </div>
                        <div className="flex items-center justify-between" data-id="element-570">
                          <span className="text-xs text-gray-700" data-id="element-571">
                            failures
                          </span>
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold ${component.severity === 'Critical' ? 'bg-red-600 text-white' : component.severity === 'High' ? 'bg-orange-500 text-white' : component.severity === 'Medium' ? 'bg-yellow-500 text-white' : 'bg-green-500 text-white'}`} data-id="element-572">
                            {component.severity}
                          </span>
                        </div>
                      </div>
                    </div>;
            })}
              </div>
            </div>)}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-573">
          <div className="flex items-center justify-between text-xs text-gray-500" data-id="element-574">
            <span data-id="element-575">Heat intensity based on failure count</span>
            <div className="flex items-center space-x-4" data-id="element-576">
              <div className="flex items-center" data-id="element-577">
                <div className="w-4 h-4 rounded bg-red-600 mr-1" data-id="element-578" />
                <span data-id="element-579">Critical (&gt;400)</span>
              </div>
              <div className="flex items-center" data-id="element-580">
                <div className="w-4 h-4 rounded bg-orange-500 mr-1" data-id="element-581" />
                <span data-id="element-582">High (300-400)</span>
              </div>
              <div className="flex items-center" data-id="element-583">
                <div className="w-4 h-4 rounded bg-yellow-500 mr-1" data-id="element-584" />
                <span data-id="element-585">Medium (150-300)</span>
              </div>
              <div className="flex items-center" data-id="element-586">
                <div className="w-4 h-4 rounded bg-green-500 mr-1" data-id="element-587" />
                <span data-id="element-588">Low (&lt;150)</span>
              </div>
            </div>
          </div>
        </div>
      </Card>
      {/* Root Cause Categories Table */}
      <Card className="p-6" data-id="element-589">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-590">
          Root Cause Categories & Linked SKUs
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-591">
          Comprehensive mapping of root causes to affected products and CAPA
          status
        </p>
        <div className="space-y-6" data-id="element-592">
          {rootCauseCategoriesData.map((category, catIndex) => <div key={catIndex} className="border-2 border-gray-200 rounded-lg overflow-hidden" data-id="element-593">
              <div className="bg-gradient-to-r from-blue-50 to-white p-4 border-b border-gray-200" data-id="element-594">
                <div className="flex items-center justify-between" data-id="element-595">
                  <div data-id="element-596">
                    <h4 className="text-md font-bold text-gray-900" data-id="element-597">
                      {category.category}
                    </h4>
                    <div className="text-sm text-gray-600" data-id="element-598">
                      {category.count} root causes identified •{' '}
                      {category.percentage}% of total
                    </div>
                  </div>
                  <div className="text-right" data-id="element-599">
                    <div className="text-2xl font-bold text-red-600" data-id="element-600">
                      ₹{category.totalCost}L
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-601">
                      Total Cost Impact
                    </div>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-602">
                <table className="w-full" data-id="element-603">
                  <thead className="bg-gray-50" data-id="element-604">
                    <tr data-id="element-605">
                      <th className="text-left py-3 px-4 text-xs font-bold text-gray-700" data-id="element-606">
                        SKU Code
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-bold text-gray-700" data-id="element-607">
                        Product
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-bold text-gray-700" data-id="element-608">
                        Root Cause Description
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-bold text-gray-700" data-id="element-609">
                        Failures
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-bold text-gray-700" data-id="element-610">
                        Cost
                      </th>
                      <th className="text-center py-3 px-4 text-xs font-bold text-gray-700" data-id="element-611">
                        CAPA Status
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-612">
                    {category.linkedSKUs.map((sku, skuIndex) => <tr key={skuIndex} className="border-t border-gray-100 hover:bg-gray-50" data-id="element-613">
                        <td className="py-3 px-4 text-sm font-mono text-blue-600" data-id="element-614">
                          {sku.sku}
                        </td>
                        <td className="py-3 px-4 text-sm font-semibold text-gray-800" data-id="element-615">
                          {sku.product}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700" data-id="element-616">
                          {sku.rootCause}
                        </td>
                        <td className="py-3 px-4 text-sm text-right font-bold text-gray-900" data-id="element-617">
                          {sku.failures}
                        </td>
                        <td className="py-3 px-4 text-sm text-right font-bold text-red-600" data-id="element-618">
                          ₹{sku.cost}L
                        </td>
                        <td className="py-3 px-4 text-center" data-id="element-619">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(sku.status)}`} data-id="element-620">
                            {sku.status}
                          </span>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>)}
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-621">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-622">
            <div className="text-center" data-id="element-623">
              <div className="text-gray-600" data-id="element-624">Total Categories</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-625">6</div>
            </div>
            <div className="text-center" data-id="element-626">
              <div className="text-gray-600" data-id="element-627">Total Root Causes</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-628">47</div>
            </div>
            <div className="text-center" data-id="element-629">
              <div className="text-gray-600" data-id="element-630">Total Cost Impact</div>
              <div className="text-xl font-bold text-red-600" data-id="element-631">₹858.6 L</div>
            </div>
            <div className="text-center" data-id="element-632">
              <div className="text-gray-600" data-id="element-633">CAPA Completed</div>
              <div className="text-xl font-bold text-green-600" data-id="element-634">12</div>
            </div>
            <div className="text-center" data-id="element-635">
              <div className="text-gray-600" data-id="element-636">CAPA In Progress</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-637">20</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default RootCauseAnalysisDashboard;